import { getBackendBaseUrl, getBackendDirectUrl, getBackendProxyPath, isUsingBackendProxy } from './axios';

// Reporte de errores del navegador del candidato al back (POST /client-errors), que
// los deja en CloudWatch con una alarma por correo. Antes un candidato con problemas
// solo se sabía si avisaba por WhatsApp.
//
// Nunca debe romper ni frenar el test: todo va en try/catch, sin esperar respuesta.

type Level = 'error' | 'info';

// Tope por carga de página y sin repetir el mismo error: un bucle de errores no debe
// inundar el log ni la alarma.
const MAX_REPORTS_PER_PAGE = 20;
let sentCount = 0;
const seen = new Set<string>();
let context: Record<string, unknown> = {};
let installed = false;

const truncate = (value: unknown, max: number) => (value == null ? undefined : String(value).slice(0, max));

// fetch y no axios: los interceptores de axios tratan un 401 como sesión del panel
// expirada, y un reporte jamás debe afectar la página del candidato. keepalive deja
// salir el reporte aunque la persona cierre o recargue la página.
const post = (base: string | undefined, payload: string, viaProxy: boolean) =>
  fetch(`${base || ''}/client-errors`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(viaProxy ? { 'X-GrowMe-Via': 'proxy' } : {}) },
    body: payload,
    keepalive: true,
  });

// Si el dominio del back está bloqueado, se intenta por el proxy del front.
// Si la vía en uso no responde, se intenta por la otra (proxy del front ↔ directa).
const send = (payload: string) => {
  const viaProxy = isUsingBackendProxy();
  post(getBackendBaseUrl(), payload, viaProxy).catch(() => {
    const proxyPath = getBackendProxyPath();
    if (!proxyPath) return;
    const other = viaProxy ? post(getBackendDirectUrl(), payload, false) : post(proxyPath, payload, true);
    other.catch(() => {});
  });
};

export const setErrorContext =(extra: Record<string, unknown>): void => {
  context = { ...context, ...extra };
};

export const reportClientError = (
  kind: string,
  error?: any,
  extra: Record<string, unknown> = {},
  level: Level = 'error'
): void => {
  try {
    const message = truncate(error?.message || error || kind, 500);
    const key = `${kind}:${message}`;
    if (seen.has(key) || sentCount >= MAX_REPORTS_PER_PAGE) return;
    seen.add(key);
    sentCount++;
    const body = {
      level,
      kind,
      message,
      stack: truncate(error?.stack, 2000),
      status: error?.response?.status,
      page: `${window.location.pathname}${window.location.search}`,
      online: navigator.onLine,
      userAgent: navigator.userAgent,
      ...context,
      ...extra,
    };
    send(JSON.stringify(body));
  } catch (reportError) {
    // Si ni el reporte se puede armar, no hay nada más que hacer.
  }
};

export const installGlobalErrorReporting = (): void => {
  if (installed) return;
  installed = true;
  window.addEventListener('error', (event) => {
    reportClientError('js_error', event.error || event.message, { source: event.filename, line: event.lineno });
  });
  window.addEventListener('unhandledrejection', (event) => {
    reportClientError('unhandled_rejection', event.reason);
  });
};
